/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter, useTranslations } from "@i18n/next";
import { useAnalytics } from "@packages/analytics";
import type { UseSessionProps } from "@packages/sdk";
import {
  customEmailAuthSchema,
  isDefaultPlan,
  useRequestCart,
  useRequestMe,
  useRequestPublicProducts,
  useRequestSubscription,
} from "@packages/sdk";
import {
  Auth,
  Container,
  cssMediaQueryMinWidthTablet,
  numericPercentages,
  numericValues,
  numericViewportWidth,
  radius,
  spacing,
  Text,
} from "@packages/ui";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

import {
  customValidator,
  UNIVERSITY_EMAIL_DOMAINS,
  UNIVERSITY_EVENTS,
  UNIVERSITY_HEADERS,
  UNIVERSITY_NAMES,
} from "./validator";

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "center",
  rowGap: spacing.m,
  width: numericViewportWidth[100],
  [cssMediaQueryMinWidthTablet]: { width: "auto" },
};
const imageStyle: Interpolation = {
  borderRadius: radius.none,
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: {
    borderRadius: radius.m,
    width: "initial",
  },
};
const formStyle: Interpolation = {
  paddingTop: spacing.l,
};
const headerStyle: Interpolation = {
  paddingInline: spacing.ml,
  [cssMediaQueryMinWidthTablet]: { paddingInline: numericValues[0] },
};
const layoutStyle: Interpolation = {
  paddingTop: spacing.none,
  [cssMediaQueryMinWidthTablet]: { paddingTop: spacing.none },
};

export const UniversityClient = ({ university }) => {
  const t = useTranslations();
  const [isMounted, setIsMounted] = useState(false);
  const { track } = useAnalytics();
  const searchParams = useSearchParams();
  const referrer = searchParams.get("referrer");
  const router = useRouter();
  const { query: products } = useRequestPublicProducts({ enabled: true }); // force using it, even if the user is auth'd for some reason
  const product = products.data?.find((product) => isDefaultPlan(product));
  const { checkCart } = useRequestCart({ priceId: product?.price.id });
  const { mutationSubscribe: subscribe } = useRequestSubscription({
    isQuery: false,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const { query: queryMe } = useRequestMe();

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    if (isMounted)
      track({
        event: UNIVERSITY_EVENTS[`${university}-view`],
        properties: {
          promo_code: referrer,
        },
      });
  }, [isMounted]);

  const handleFormSubmission = ({ email }) => {
    setLoading(true);
    track({
      event: UNIVERSITY_EVENTS[`${university}-submit`],
      properties: {
        name: undefined,
        last_name: undefined,
        email,
        referrer,
      },
    });
  };

  const handleVerification = ({ email }) => {
    track({
      event: UNIVERSITY_EVENTS[`${university}-captcha`],
      properties: {
        name: undefined,
        last_name: undefined,
        email,
        referrer,
      },
    });
  };

  const handleAuth: UseSessionProps["onAuthenticated"] = async ({
    isNewUser,
  }) => {
    if (isNewUser) {
      track({
        event: UNIVERSITY_EVENTS[`${university}-create`],
        properties: {
          name: queryMe?.data?.name,
          last_name: queryMe?.data?.last_name,
          email: queryMe?.data?.email,
          referrer,
        },
      });
    }

    if (!queryMe?.data?.subscription) {
      try {
        const cart = await checkCart.mutateAsync({ promoCode: referrer });
        await subscribe.mutateAsync({
          priceId: cart?.product?.price?.id ?? product?.price?.id ?? undefined,
          promoCode: referrer,
          paymentMethod: null,
        });

        track({
          event: UNIVERSITY_EVENTS[`${university}-subscribe`],
          properties: {
            name: queryMe.data.name,
            last_name: queryMe.data.last_name,
            email: queryMe.data.email,
            referrer,
          },
        });
      } catch (e) {
        console.error(e);
      }
    }
    router.push("/home");
  };

  return (
    <Container maxWidth size={"xs"} css={containerStyle}>
      <Image
        src={"https://images.hallow.app/assets/college.webp"}
        alt={""}
        width={500}
        height={350}
        css={imageStyle}
      />
      <Text as={"h1"} type={"headline"} size={"m"} css={headerStyle}>
        {t(UNIVERSITY_HEADERS[university])}
      </Text>
      <Text as={"h2"} type={"body"} size={"l"} css={headerStyle}>
        {t("register_button_sign_up_with_email", {
          "0": UNIVERSITY_NAMES[university],
          "1": UNIVERSITY_EMAIL_DOMAINS[university],
        })}
      </Text>
      <Auth
        onAuth={handleAuth}
        onFormSubmit={handleFormSubmission}
        onCaptchaVerify={handleVerification}
        onError={() => setLoading(false)}
        allowedMethods={["email"]}
        customSchema={customEmailAuthSchema(
          customValidator(UNIVERSITY_EMAIL_DOMAINS[university]),
          /* this will still throw errors when entering data, but we can't pass a generic string as the error
           * message in this case, so this will have to do */
          t(
            "web_form_error_invalid_domain_email",
            UNIVERSITY_EMAIL_DOMAINS[university],
          ),
        )}
        css={formStyle}
        layoutCSS={layoutStyle}
        formDisabled={loading}
      />
    </Container>
  );
};
